import React from "react"
import { useTranslation } from "react-i18next"

import type {
  BillPdfAnalysisDetailVariant,
  BillPdfAnalysisRecord,
  BillPdfAnalysisRecordField,
} from "@/modules/data/services/use-bill-pdf-upload-session-analysis"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { getDisplayValue } from "@/utils/value"
import { startCase } from "lodash-es"

import {
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material"

interface BillPdfPreviewAnalysisDetailDialogProps {
  onClose: () => void
  open: boolean
  records: BillPdfAnalysisRecord[]
  variant: BillPdfAnalysisDetailVariant | null
}

const getRecordDisplayValue = (
  value: BillPdfAnalysisRecordField | undefined
): string => {
  if (typeof value === "string" || typeof value === "number") {
    return getDisplayValue(value)
  }

  if (typeof value === "boolean") {
    return String(value)
  }

  return getDisplayValue(null)
}

const collectRecordKeys = (records: BillPdfAnalysisRecord[]): string[] => {
  const keys = records.flatMap((record) => Object.keys(record))
  return [...new Set(keys)].sort((left, right) => left.localeCompare(right))
}

const getRecordRowKey = (record: BillPdfAnalysisRecord): string =>
  Object.keys(record)
    .sort((left, right) => left.localeCompare(right))
    .map((key) => `${key}:${getRecordDisplayValue(record[key])}`)
    .join("|")

interface KeyedAnalysisRecord {
  record: BillPdfAnalysisRecord
  rowKey: string
}

const toKeyedRecords = (
  records: BillPdfAnalysisRecord[]
): KeyedAnalysisRecord[] => {
  const seen = new Map<string, number>()

  return records.map((record) => {
    const baseKey = getRecordRowKey(record)
    const occurrence = seen.get(baseKey) ?? 0
    seen.set(baseKey, occurrence + 1)

    return {
      record,
      rowKey: occurrence === 0 ? baseKey : `${baseKey}-${occurrence}`,
    }
  })
}

const getDialogTitleKey = (
  variant: BillPdfAnalysisDetailVariant | null
): string =>
  variant === "overlaps"
    ? "upload.billPdf.analysis.overlappingPeriods"
    : "upload.billPdf.analysis.gaps"

export const BillPdfPreviewAnalysisDetailDialog: React.FC<
  BillPdfPreviewAnalysisDetailDialogProps
> = ({ onClose, open, records, variant }) => {
  const { t } = useTranslation("data")
  const columns = collectRecordKeys(records)
  const keyedRecords = toKeyedRecords(records)

  return (
    <Dialog
      aria-labelledby="bill-pdf-preview-analysis-detail-title"
      data-testid="bill-pdf-preview-analysis-detail-dialog"
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open && variant != null}
    >
      <DialogTitle
        id="bill-pdf-preview-analysis-detail-title"
        onClose={onClose}
      >
        {t(getDialogTitleKey(variant))}
      </DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{startCase(column)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {keyedRecords.map(({ record, rowKey }) => (
              <TableRow key={rowKey}>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {getRecordDisplayValue(record[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  )
}
